import React, { useRef } from "react";
import AccData from "../../data/accountStatement";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";
import { LuDownload } from "react-icons/lu";

const AccountStatement = () => {
  // Reference to the table element for PDF generation
  const tableRef = useRef(null);

  // Hook for translation support
  const { t } = useTranslation();

  /**
   * Generate a PDF document of the account statement table.
   * Uses html2canvas to convert the HTML table to a canvas, 
   * then adds the content to a jsPDF document.
   */
  const generatePDF = async () => {
    const input = tableRef.current;

    // Ensure the table exists before proceeding
    if (!input) return;

    // Convert the HTML content to a canvas with higher resolution
    const canvas = await html2canvas(input, {
      scale: 3, // Higher scale ensures better quality in the PDF
      useCORS: true, // Handle cross-origin images
      scrollX: -window.scrollX, // Adjust for any horizontal scrolling
      scrollY: -window.scrollY, // Adjust for any vertical scrolling
    });

    const imgData = canvas.toDataURL("image/png");

    // Create a new jsPDF document with A4 page size
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    // Add content to the PDF, splitting across pages if necessary
    while (heightLeft > 0) {
      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pageHeight;
      position -= pageHeight;

      // Add a new page if there is remaining content
      if (heightLeft > 0) {
        pdf.addPage();
      }
    }

    // Save the generated PDF with a filename
    pdf.save("AccountStatement.pdf");
  };

  return (
    <>
      {/* Header Section */}
      <div className="flex justify-center my-3 flex-row gap-5">
        <p className="text-white text-lg sm:text-xl md:text-2xl font-medium text-center">
          {t("lotteryAccountDetails.headerTitle")}
        </p>

        {/* Download Button with Icon */}
        <div className="flex justify-end">
          <button onClick={generatePDF}>
            <LuDownload className="text-white text-3xl cursor-pointer " />
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="px-4 sm:px-10">
        <div ref={tableRef}>
          <table className="table-auto w-full max-w-full mx-auto bg-white border border-gray-200 text-[6px] sm:text-[18px] md:text-[12]">
            {/* Table Header */}
            <thead>
              <tr className="bg-gray-200 text-black">
                <th className="text-left px-2 py-1">Date</th>
                <th className="text-left px-2 py-1">Description</th>
                <th className="text-left px-2 py-1">Ticket Cost (Debit)</th>
                <th className="text-left px-2 py-1">Winning Amount (Credit)</th>
                <th className="text-left px-2 py-1">Balance</th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {AccData.map((row, index) => (
                <tr
                  key={index}
                  className={`border-b hover:bg-gray-100 ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <td className="px-2 py-1 text-black">{row.date}</td>
                  <td className="px-2 py-1 text-black">{row.description}</td>
                  <td className="px-2 py-1 text-black">{row.ticketCost}</td>
                  <td className="px-2 py-1 text-black">{row.winningAmount}</td>
                  <td className="px-2 py-1 text-black">{row.balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default AccountStatement;
